// Drone voices. All partials are exact harmonics so the perceived pitch stays true.
export const TIMBRES = [
  { id: 'organ', label: 'Organo' },
  { id: 'strings', label: 'Archi' },
  { id: 'shruti', label: 'Shruti box' },
  { id: 'tanpura', label: 'Tanpura' },
  { id: 'pad', label: 'Pad morbido' },
];

const SPEC = {
  organ: { amps: [0, 1, 0.62, 0.36, 0.3, 0.12, 0.15, 0, 0.1], detune: [0], cutoff: 3200, attack: 0.06, level: 1 },
  strings: { type: 'sawtooth', detune: [-5, 0, 5], cutoff: 2000, attack: 0.35, level: 0.5 },
  shruti: { amps: [0, 1, 0.85, 0.7, 0.55, 0.5, 0.4, 0.33, 0.26, 0.2, 0.16, 0.12, 0.09], detune: [0, 1.5], cutoff: 2400, attack: 0.25, level: 0.75 },
  pad: { amps: [0, 1, 0.25, 0.08, 0.03], detune: [-4, 4], cutoff: 1100, attack: 0.45, level: 1.1 },
};
const JAWARI = [0, 1, 0.7, 0.65, 0.5, 0.45, 0.42, 0.35, 0.3, 0.28, 0.22, 0.2, 0.16, 0.14, 0.1, 0.08];
const GAP = 0.65;
const AHEAD = 2;

const waveCache = new WeakMap();
const wave = (ctx, amps) => {
  const map = waveCache.get(ctx) || new Map();
  waveCache.set(ctx, map);
  if (!map.has(amps)) map.set(amps, ctx.createPeriodicWave(new Float32Array(amps.length), Float32Array.from(amps)));
  return map.get(amps);
};
const voiceGain = (vi, n) => (vi ? 0.22 * Math.min(1, 1.5 / (n - 1)) : 0.32);

function sustained(ctx, out, freqs, spec, t) {
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = spec.cutoff;
  lp.Q.value = 0.6;
  lp.connect(out);
  const oscs = [];
  freqs.forEach((f, vi) => {
    const g = ctx.createGain();
    g.gain.value = (voiceGain(vi, freqs.length) * spec.level) / spec.detune.length;
    g.connect(lp);
    spec.detune.forEach((d) => {
      const o = ctx.createOscillator();
      if (spec.amps) o.setPeriodicWave(wave(ctx, spec.amps)); else o.type = spec.type;
      o.frequency.value = f;
      o.detune.value = d;
      o.connect(g);
      o.start(t);
      oscs.push(o);
    });
  });
  return { attack: spec.attack, stop: (at) => oscs.forEach((o) => o.stop(at)) };
}

// Plucked cycle (chord tones, upper root twice, low root) with a lookahead scheduler.
function tanpura(ctx, out, freqs, at) {
  const root = freqs[0];
  const cycle = [...(freqs.length > 1 ? freqs.slice(1) : [root * 2]), root * 2, root * 2, root];
  const live = new Set();
  let next = at + 0.003;
  let i = 0;
  const pluck = (f, at) => {
    const o = ctx.createOscillator();
    const lp = ctx.createBiquadFilter();
    const g = ctx.createGain();
    o.setPeriodicWave(wave(ctx, JAWARI));
    o.frequency.value = f;
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(4500, at);
    lp.frequency.setTargetAtTime(1300, at, 0.8);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.34, at + 0.012);
    g.gain.setTargetAtTime(0, at + 0.012, 1.1);
    o.connect(lp).connect(g).connect(out);
    o.start(at);
    o.stop(at + 4.5);
    live.add(o);
    o.onended = () => live.delete(o);
  };
  const tick = () => {
    while (next < ctx.currentTime + AHEAD) {
      const k = i % cycle.length;
      pluck(cycle[k], next);
      next += k === cycle.length - 1 ? GAP * 2 : GAP;
      i += 1;
    }
  };
  tick();
  const id = setInterval(tick, 400);
  return {
    attack: 0.02,
    stop: (at) => { clearInterval(id); live.forEach((o) => { try { o.stop(at); } catch { /* already stopped */ } }); },
  };
}

export const createVoice = (ctx, out, freqs, timbre, at) =>
  (timbre === 'tanpura' ? tanpura(ctx, out, freqs, at) : sustained(ctx, out, freqs, SPEC[timbre] || SPEC.organ, at));
